<template>
    <div class="modal fade" id="extendLicenseModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered extend-license-dialog">
            <div class="modal-content" v-if="props.license">
                <div class="modal-header border-0 pb-0">
                    <div>
                        <h1 class="modal-title fs-5">Extend License</h1>
                        <p class="text-sm text-secondary mb-0">Choose a duration and add a coupon if you have one.</p>
                    </div>
                    <button ref="closeButton" type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body pt-4">
                    <p class="text-xs font-semibold uppercase tracking-wide text-secondary mb-2">Extend by</p>
                    <div class="row g-2 mb-4">
                        <div class="col-4" v-for="option in extensionOptions" :key="option.value">
                            <button
                                type="button"
                                class="btn w-100 h-100 py-3 duration-option"
                                :class="extendType === option.value ? 'btn-primary' : 'btn-outline-secondary'"
                                :disabled="option.price === null"
                                @click="extendType = option.value"
                            >
                                <span class="d-block fw-semibold">{{ option.label }}</span>
                                <span class="d-block mt-1">{{ option.price === null ? 'Unavailable' : formatCurrency(option.price) }}</span>
                            </button>
                        </div>
                    </div>

                    <label for="extensionCoupon" class="form-label text-sm fw-semibold">Coupon code <span class="text-secondary fw-normal">(optional)</span></label>
                    <div class="input-group mb-2">
                        <input
                            id="extensionCoupon"
                            v-model.trim="couponCode"
                            type="text"
                            autocomplete="off"
                            class="form-control shadow-none text-black"
                            placeholder="Coupon code"
                            @keyup.enter="onApplyCoupon"
                        />
                        <button
                            type="button"
                            class="btn btn-outline-primary"
                            :disabled="quoteLoading || !selectedOption || !normalizedCouponCode"
                            @click="onApplyCoupon"
                        >
                            {{ quoteLoading ? 'Applying...' : 'Apply' }}
                        </button>
                    </div>
                    <p v-if="quoteIsCurrent" class="text-sm text-success mb-4">
                        Coupon applied. You save {{ formatCurrency(appliedQuote.discountAmount) }}.
                    </p>
                    <p v-else class="text-xs text-secondary mb-4">
                        Apply a coupon to confirm the discounted price before extending.
                    </p>

                    <div class="rounded border p-3 extension-summary">
                        <div v-if="quoteIsCurrent" class="d-flex justify-content-between align-items-center mb-2">
                            <span class="text-secondary">Original price</span>
                            <span class="text-decoration-line-through">{{ formatCurrency(selectedOption.price) }}</span>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="text-secondary">Selected price</span>
                            <strong>{{ selectedOption ? formatCurrency(displayedPrice) : 'Unavailable' }}</strong>
                        </div>
                        <div class="d-flex justify-content-between align-items-center">
                            <span class="text-secondary">Your balance</span>
                            <strong>{{ formatCurrency(balance) }}</strong>
                        </div>
                        <p class="text-xs text-secondary mb-0 mt-2">The final charge is checked securely by the server.</p>
                    </div>
                </div>

                <div class="modal-footer border-0 pt-0">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button @click="onExtend" :disabled="buttonDisableFlag || !selectedOption || couponNeedsApply" class="btn btn-primary">
                        {{ buttonDisableFlag ? 'Extending...' : 'Extend' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAccountStore, useProxyStore } from "@/stores"

const extendType = ref('MONTH');
const couponCode = ref('');
const appliedQuote = ref(null);
const quoteLoading = ref(false);
const buttonDisableFlag = ref(false);
const closeButton = ref(null);
const proxyStore = useProxyStore();
const accountStore = useAccountStore();
const { user } = storeToRefs(accountStore);

const props = defineProps({
    license: {
        type: Object,
        required: true
    }
});
const emit = defineEmits(['close']);

const numericPrice = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const price = Number(value);
    return Number.isFinite(price) && price > 0 ? price : null;
};

const extensionOptions = computed(() => [
    { value: 'MONTH', label: 'Month', price: numericPrice(props.license?.month_price) },
    { value: 'WEEK', label: 'Week', price: numericPrice(props.license?.week_price) },
    { value: 'DAY', label: 'Day', price: numericPrice(props.license?.day_price) },
]);
const selectedOption = computed(() => extensionOptions.value.find(option => option.value === extendType.value && option.price !== null) || null);
const normalizedCouponCode = computed(() => couponCode.value.trim());
const quoteIsCurrent = computed(() => Boolean(
    appliedQuote.value &&
    appliedQuote.value.license === props.license?.uuid &&
    appliedQuote.value.timeUnit === extendType.value &&
    appliedQuote.value.couponCode === normalizedCouponCode.value &&
    Math.round(Number(appliedQuote.value.basePrice) * 100) === Math.round(Number(selectedOption.value?.price) * 100)
));
const displayedPrice = computed(() => quoteIsCurrent.value
    ? Number(appliedQuote.value.finalPrice)
    : selectedOption.value?.price);
const couponNeedsApply = computed(() => Boolean(normalizedCouponCode.value) && !quoteIsCurrent.value);
const balance = computed(() => Number(user.value?.balance || 0));
const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

watch(() => props.license?.uuid, () => {
    couponCode.value = '';
    appliedQuote.value = null;
    const preferred = extensionOptions.value.find(option => option.value === 'MONTH' && option.price !== null)
        || extensionOptions.value.find(option => option.price !== null);
    extendType.value = preferred?.value || 'MONTH';
}, { immediate: true });

watch([couponCode, extendType], () => {
    appliedQuote.value = null;
});

const onApplyCoupon = async () => {
    if (!selectedOption.value || !normalizedCouponCode.value || quoteLoading.value) return;

    const requestedLicense = props.license.uuid;
    const requestedTimeUnit = extendType.value;
    const requestedCouponCode = normalizedCouponCode.value;
    quoteLoading.value = true;
    const quote = await proxyStore.quoteExtension(
        requestedLicense,
        requestedTimeUnit,
        requestedCouponCode
    );
    quoteLoading.value = false;

    if (quote?.couponApplied &&
        props.license.uuid === requestedLicense &&
        extendType.value === requestedTimeUnit &&
        normalizedCouponCode.value === requestedCouponCode) {
        appliedQuote.value = {
            ...quote,
            license: requestedLicense,
            timeUnit: requestedTimeUnit,
            couponCode: requestedCouponCode,
        };
    }
};

const onExtend = async () => {
    if (!selectedOption.value || couponNeedsApply.value) return;

    buttonDisableFlag.value = true;
    const succeeded = await proxyStore.extendLicense(
        props.license.uuid,
        extendType.value,
        normalizedCouponCode.value,
        quoteIsCurrent.value ? displayedPrice.value : null
    );
    buttonDisableFlag.value = false;

    if (succeeded) {
        closeButton.value?.click();
        emit('close');
    }
};
</script>

<style scoped>
.extend-license-dialog {
    width: min(560px, calc(100vw - 2rem));
    max-width: 560px;
}

.duration-option {
    min-height: 82px;
}

.extension-summary {
    background: rgba(127, 127, 127, 0.06);
}
</style>
